import { useCallback, useEffect, useState } from 'react';
import { StandardSalesLinesEditModal } from '../../components/dem/StandardSalesLinesEditModal';
import { PageToolbar } from '../../components/ui/PageToolbar';
import { useDemMasterDataRealtime } from '../../hooks/useDemMasterDataRealtime';
import {
  listSslTemplates,
  type DemSslTemplateSummary,
} from '../../lib/demStandardSalesLines';

export function DemStandardSalesLinesPage() {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [rows, setRows] = useState<DemSslTemplateSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [isNew, setIsNew] = useState(false);
  const [selectedCode, setSelectedCode] = useState<string | null>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await listSslTemplates({
        search: debouncedSearch,
      });
      setRows(result.rows);
      setTotal(result.total);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load standard sales lines.');
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch]);

  useEffect(() => {
    void load();
  }, [load]);

  useDemMasterDataRealtime({
    include: ['standard_sales_lines'],
    onChange: () => {
      void load();
    },
  });

  const openExisting = (code: string) => {
    setSelectedCode(code);
    setIsNew(false);
    setModalOpen(true);
  };

  const openNew = () => {
    setSelectedCode(null);
    setIsNew(true);
    setModalOpen(true);
  };

  return (
    <div className="dem-ssl-page dem-ssl-page--floating">
      <div className="dem-floating-page-actions dem-floating-page-actions--end">
        <div className="dem-floating-page-actions__btns">
          <button
            type="button"
            className="btn-secondary btn-sm"
            disabled={loading}
            onClick={() => void load()}
          >
            Refresh
          </button>
          <button type="button" className="btn-primary btn-sm" onClick={openNew}>
            New template
          </button>
        </div>
      </div>

      <PageToolbar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search by code or customer name"
        leading={
          <span className="page-count">
            {loading
              ? 'Loading…'
              : `${total.toLocaleString()} template${total === 1 ? '' : 's'}`}
          </span>
        }
      />

      {error ? <p className="alert alert-error">{error}</p> : null}

      {loading && rows.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state__spinner" />
          <p>Loading standard sales lines…</p>
        </div>
      ) : rows.length === 0 ? (
        <div className="empty-state">
          <p>
            {debouncedSearch
              ? 'No templates match your search.'
              : 'No standard sales lines yet. Create a template to get started.'}
          </p>
        </div>
      ) : (
        <div className="card dem-ssl-list">
          <div className="dem-ssl-list__head" role="row">
            <span>Template code</span>
            <span>Customer name</span>
            <span>Lines</span>
          </div>
          {rows.map((row) => (
            <button
              key={row.template_code}
              type="button"
              className={`dem-ssl-list__row${
                modalOpen && !isNew && selectedCode === row.template_code ? ' is-selected' : ''
              }`}
              onClick={() => openExisting(row.template_code)}
            >
              <span className="dem-ssl-list__code">{row.template_code}</span>
              <span className="dem-ssl-list__name" title={row.customer_name || undefined}>
                {row.customer_name || '—'}
              </span>
              <span className="dem-ssl-list__count">
                {row.line_count.toLocaleString()}
              </span>
            </button>
          ))}
        </div>
      )}

      <StandardSalesLinesEditModal
        open={modalOpen}
        templateCode={selectedCode}
        isNew={isNew}
        onClose={() => setModalOpen(false)}
        onSaved={() => void load()}
        onDeleted={() => void load()}
      />
    </div>
  );
}
